import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import '@adornis/chemistry/elements/components/x-link.js';
import { XSidebar } from '@adornis/chemistry/elements/components/x-sidebar.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import '@adornis/mails/client/x-mails-template-editor.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { searchParamsSignal } from '@adornis/router/client/routing-state-signal.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { currentUser, impersonatingUser } from '@adornis/users/client/currentUser.js';
import { stopImpersonationOfUser } from '@adornis/users/db/a-user.js';
import { config } from '@ils/config/config.js';
import { getLogo } from '@ils/examType/logos.js';
import '@ils/export/client/ils-export.js';
import '@ils/locations/client/ils-locations.js';
import '@ils/osces/client/ils-editor-osce-group.js';
import '@ils/osces/client/ils-editor-osce.js';
import { permissionGuard } from '@ils/permissions/client/permissionGuard.js';
import { hasPermission } from '@ils/permissions/hasPermission.js';
import { hasRole } from '@ils/permissions/hasRole.js';
import { getAllowedPageKeys } from '@ils/permissions/pagePermission.js';
import { prettyUserRole, UserRole } from '@ils/static/enums.js';
import { computed, SignalWatcher } from '@lit-labs/preact-signals';
import { html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
// @ts-expect-error we can do this.
import '@ils/evaluation-scheduler/client/ils-evaluation-timeallocation.js';
import { version } from '../../../package.json';
import type { ILSUser } from '../../db/ils-user.js';
import '../components/ils-examday-selector.js';
import '../components/ils-product-card.js';
import { ILSSpeedtestDialog } from '../components/ils-speedtest-dialog.js';
import '../components/video/ils-video-check-in.js';
import { isSidebarOpen } from '../global-state.js';
import { logout } from '../logout.js';
import { getUserAllowedPages, pages, type pageType } from '../sidebarEntries.js';
import './evaluation/ils-evaluation-async.js';
import './evaluation/ils-evaluation-overview.js';
import './evaluation/ils-evaluation-reported-errors.js';
import './evaluation/ils-evaluation-sync.js';
import './evaluation/ils-evaluation-termin.js';
import './ils-dashboard.js';
import './ils-editor-exam.js';
import './ils-exam.js';
import './ils-examday-overview.js';
import './ils-local-recordings.js';
// import './ils-mails-editor.js';
import { getAllEntities } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { MailTemplate } from '@adornis/mails/db/mail-template.js';
import { sendTestMailToCurrentUser } from '@ils/mails/api/mails.js';
import { ILSServerTime } from '../components/ils-server-time.js';
import './ils-scheduling.js';
import './ils-spectate.js';
import './ils-tester-dashboard.js';
import './ils-users.js';
import './ils-view-osces.js';

export const _sidebarWidthOpen = '350px';
export const _sidebarWidthClosed = '76px';
export const _topbarHeight = '110px';

const pagePermission = stateful(({ page, render }: { page: string; render: (user: ILSUser) => any }) => {
  const user = useObservable(() => currentUser<ILSUser>(), []);
  if (user && getAllowedPageKeys(user).includes(page)) return render(user);

  return html`
    <x-empty-placeholder
      flex
      .text=${'Keine Berechtigungen um diese Seite zu sehen'}
      .img=${'close'}
      back-text="Zurück"
    ></x-empty-placeholder>
  `;
});

const getMailTemplates = async () => getAllEntities(MailTemplate)(selectionSet(() => MailTemplate, 2));

export const startRouter = makeRouter(
  {
    root: {
      path: '/',
      render: () => html`
        <x-empty-placeholder flex .text=${'Navigieren Sie über die linke Leiste!'} .img=${'home'}></x-empty-placeholder>
      `,
    },
    users: {
      path: '/users',
      render: () => pagePermission({ page: 'users', render: () => html` <ils-users></ils-users> ` }),
    },
    locations: {
      path: '/locations',
      render: () => pagePermission({ page: 'locations', render: () => html` <ils-locations></ils-locations> ` }),
    },
    'editor-osce': {
      path: '/editor-osce/:osceID?',
      render: params =>
        pagePermission({
          page: 'editor-osce',
          render: () => {
            if (params.osceID) return html` <ils-editor-osce osce-id=${params.osceID}></ils-editor-osce> `;
            return html` <ils-view-osces editable ${css({ height: '100%' })}></ils-view-osces> `;
          },
        }),
    },
    'editor-osce-group': {
      path: '/editor-osce-group/:osceGroupID?',
      render: params =>
        pagePermission({
          page: 'editor-osce-group',
          render: () => {
            if (params.osceGroupID)
              return html` <ils-editor-osce-group osce-group-id=${params.osceGroupID}></ils-editor-osce-group> `;
            return html` <ils-view-osces editable ${css({ height: '100%' })}></ils-view-osces> `;
          },
        }),
    },
    'editor-exam': {
      path: '/editor-exam/:examDayID?',
      render: params =>
        pagePermission({
          page: 'editor-exam',
          render: () => {
            if (params.examDayID) return html` <ils-editor-exam examday-id=${params.examDayID}></ils-editor-exam> `;
            return html` <ils-examday-overview></ils-examday-overview> `;
          },
        }),
    },
    scheduling: {
      path: '/scheduling',
      render: () => pagePermission({ page: 'scheduling', render: () => html` <ils-scheduling></ils-scheduling> ` }),
    },
    dashboard: {
      path: '/dashboard',
      render: () => pagePermission({ page: 'dashboard', render: () => html` <ils-dashboard></ils-dashboard> ` }),
    },
    'check-in': {
      path: '/check-in',
      render: () =>
        pagePermission({
          page: 'check-in',
          render: () => html` <ils-video-check-in flex ${css({ minHeight: '0' })}></ils-video-check-in> `,
        }),
    },
    exam: {
      path: '/exam',
      render: () => pagePermission({ page: 'exam', render: () => html` <ils-exam></ils-exam> ` }),
    },
    // TODO maybe evaluation router?
    'evaluation-overview': {
      path: '/evaluation-overview',
      render: () =>
        pagePermission({
          page: 'evaluation-overview',
          render: user => {
            return hasPermission(user, 'ratings', 'viewoverview')
              ? html` <ils-evaluation-overview></ils-evaluation-overview> `
              : html` <ils-evaluation-overview-inspector></ils-evaluation-overview-inspector> `;
          },
        }),
    },
    'evaluation-timeallocation': {
      path: '/evaluation-timeallocation',
      render: () =>
        pagePermission({
          page: 'evaluation-timeallocation',
          render: () => html` <ils-evaluation-timeallocation></ils-evaluation-timeallocation> `,
        }),
    },
    'evaluation-reported-errors': {
      path: '/evaluation-reported-errors',
      render: () =>
        pagePermission({
          page: 'evaluation-reported-errors',
          render: () => html` <ils-evaluation-reported-errors></ils-evaluation-reported-errors> `,
        }),
    },
    'evaluation-async': {
      path: '/evaluation-async/:slotID?',
      render: params =>
        pagePermission({
          page: 'evaluation-async',
          render: () => html` <ils-evaluation-async .slotID=${params.slotID}></ils-evaluation-async> `,
        }),
    },
    'evaluation-sync': {
      path: '/evaluation-sync/:slotID?',
      render: params =>
        pagePermission({
          page: 'evaluation-sync',
          render: () => html` <ils-evaluation-sync .slotID=${params.slotID}></ils-evaluation-sync> `,
        }),
    },
    'evaluation-termin': {
      path: '/evaluation-termin/:terminID?',
      render: params =>
        pagePermission({
          page: 'evaluation-termin',
          render: () => html` <ils-evaluation-termin .terminID=${params.terminID}></ils-evaluation-termin> `,
        }),
    },
    // TODO end evaluation router?
    'local-recordings': {
      path: '/local-recordings',
      render: () =>
        pagePermission({
          page: 'local-recordings',
          render: () => html` <ils-local-recordings></ils-local-recordings> `,
        }),
    },
    mails: {
      path: '/mails',
      render: () =>
        pagePermission({
          page: 'mails',
          render: user =>
            html`
              <x-flex padding="md" flex>
                <x-mails-template-editor
                  ${css({ height: '100%' })}
                  .getMailTemplates=${getMailTemplates}
                  .sendTestMail=${async (type: string, scope: string) => {
                    await sendTestMailToCurrentUser(type, scope)();
                    void XSnackbar.show('Testmail gesendet');
                  }}
                  attachments
                  footer
                  ?disabled=${!hasPermission(user, 'mails', 'modify')}
                ></x-mails-template-editor>
              </x-flex>
            `,
        }),
    },
    spectate: {
      path: '/spectate/:slotID?',
      render: params =>
        pagePermission({
          page: 'spectate',
          render: () => html` <ils-spectate slot-id=${params.slotID}></ils-spectate> `,
        }),
    },
    'content-osce': {
      path: '/content-osce/:osceID?',
      render: params =>
        pagePermission({
          page: 'content-osce',
          render: () => html` <ils-view-osces osce-id=${params.osceID} ${css({ height: '100%' })}></ils-view-osces> `,
        }),
    },
    export: {
      path: '/export',
      render: () => pagePermission({ page: 'export', render: () => html` <ils-export></ils-export> ` }),
    },
  },
  {
    fallback: () => html`
      <x-empty-placeholder flex .text=${'Navigieren Sie über die linke Leiste!'} .img=${'home'}></x-empty-placeholder>
    `,
  },
);

const startRouterPagename = computed(() => {
  try {
    const routeName = startRouter.currentRoute.value.route.name;
    if (routeName === 'root') return 'Dashboard';
    return routeName && pages[routeName]
      ? clientTranslator.translate('page', pages[routeName].pageKey, 'title')
      : '404 - Page not Found';
  } catch (error: any) {
    return '404 - Page not Found because of error';
  }
});

@customElement('ils-start')
export class ILSStartPage extends SignalWatcher(ChemistryLitElement) {
  override styles() {
    return [{ ':host': { height: '100%', width: '100%' } }];
  }

  @state() public user = new RXController(this, currentUser<ILSUser>());
  @state() public impersonating = new RXController(this, impersonatingUser<ILSUser>());

  @state() size = new WindowSizeController(this);

  @state() sidebarOpen = new RXController(this, isSidebarOpen);

  override connectedCallback() {
    super.connectedCallback();

    void currentUser<ILSUser>().result.then(user => {
      if (!hasRole(user, UserRole.TESTER)) return;
      let lastSpeedTestConsentValid = false;
      try {
        const item = localStorage.getItem('speedTestConsent');
        lastSpeedTestConsentValid = !!item && DateTime.fromISO(JSON.parse(item)).plus({ day: 1 }) > DateTime.now();
      } catch {
        /* nothing to do */
      }
      if (!lastSpeedTestConsentValid)
        setTimeout(() => ILSSpeedtestDialog.showPopup({ modal: true }).catch(e => console.warn(e)), 3000);
    });
  }

  override render() {
    if (this.user.isLoading) return html` <x-loading-state no-wrapper></x-loading-state> `;
    if (!this.user.value) {
      return nothing;
    }

    return html`
      <x-flex horizontal ${css({ height: '100%', width: '100%' })}>
        ${this.sidebar()}
        <x-flex flex ${css({ minWidth: '0' })}>
          ${this.topbar()}
          <x-flex
            flex
            ${css({
              width: '100%',
              minHeight: '0',
              overflow: 'auto',
              background: this.colors.white,
            })}
          >
            ${startRouter.outlet.value}
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  }

  private sidebar() {
    if (!this.user.value) return nothing;
    return html`
      <x-sidebar
        ${css({
          borderRight: `1px solid ${this.colors.neutral.shade(200)}`,
          width: !this.sidebarOpen.value ? '70px' : '',
        })}
      >
        <img
          disabled
          slot="logo"
          src=${this.sidebarOpen.value ? getLogo('big') : getLogo('small')}
          ${css({ width: this.sidebarOpen.value ? '200px' : '80%', margin: '0 auto' })}
        />
        ${this._sidebarToggleAndTime()}
        <x-flex
          ?crossaxis-center=${!this.sidebarOpen.value}
          ${css({
            borderTop: '1px solid',
            borderBottom: '1px solid',
            borderColor: this.colors.neutral.shade(100),
            padding: `${this.spacing.sm} 0`,
          })}
          space="xs"
        >
          ${repeat(
            getUserAllowedPages(this.user.value).filter(page => page.location === 'specific'),
            page => this.sidebarElement(page),
          )}
        </x-flex>
        ${permissionGuard({
          resource: 'examdays',
          action: 'viewselect',
          guardedTemplate: html`
            <x-flex
              center
              ?crossaxis-center=${!this.sidebarOpen.value}
              padding=${this.sidebarOpen.value ? 'md' : ''}
              @click=${() => (!this.sidebarOpen.value ? isSidebarOpen.next(true) : '')}
              ${css({
                cursor: 'pointer',
                ...(!this.sidebarOpen.value ? { padding: `${this.spacing.sm} 0` } : {}),
              })}
            >
              ${this.sidebarOpen.value
                ? html` <ils-examday-selector main-selector></ils-examday-selector> `
                : html`
                    <x-link
                      @click=${() => isSidebarOpen.next(true)}
                      ${css({
                        userSelect: 'none',
                      })}
                    >
                      <x-flex center crossaxis-center padding="sm">
                        <x-icon ${css({ fontSize: '22px' })}> calendar_today </x-icon>
                        <x-tooltip> <x-text> Prüfungstag auswählen </x-text> </x-tooltip>
                      </x-flex>
                    </x-link>
                  `}
            </x-flex>
          `,
        })}
        <x-flex
          ?crossaxis-center=${!this.sidebarOpen.value}
          ${css({ borderTop: `1px solid ${this.colors.neutral.shade(100)}`, padding: `${this.spacing.sm} 0` })}
          space="xs"
        >
          ${repeat(
            getUserAllowedPages(this.user.value).filter(e => e.location === 'general'),
            page => this.sidebarElement(page),
          )}
        </x-flex>
        <x-flex w100 space="sm" slot="bottom" padding="sm" ?crossaxis-center=${!this.sidebarOpen.value}>
          ${this.impersonating.value
            ? html`
                <x-button
                  mode=${this.sidebarOpen.value ? 'text' : 'icon'}
                  icon="person_off"
                  @click=${async () => {
                    await stopImpersonationOfUser()();
                    window.location.reload();
                  }}
                >
                  Stop impersonating
                </x-button>
              `
            : ''}
          <x-flex space="xs">
            <x-button
              @click=${() => (this.user.value ? logout() : undefined)}
              mode=${this.sidebarOpen.value ? 'text' : 'icon'}
              icon="logout"
            >
              Logout
            </x-button>
            <x-text ${css({ color: this.colors.tone.subtle, marginLeft: 'auto' })}> v${version} </x-text>
          </x-flex>
          <x-tooltip>
            <x-flex space="xs">
              <x-text bold> Aktuell eingeloggt als: </x-text>
              <x-text> ${this.user.value.ecommerce?.fullName} </x-text>
              <x-text> ${this.user.value.roles.map(role => prettyUserRole(role.name as UserRole)).join(', ')} </x-text>
            </x-flex>
          </x-tooltip>
        </x-flex>
      </x-sidebar>
    `;
  }

  private _sidebarToggleAndTime() {
    return html`
      <x-flex
        horizontal
        ?center=${!this.sidebarOpen.value}
        flex
        crossaxis-end
        space="md"
        ${css({
          width: '100%',
          paddingLeft: this.sidebarOpen.value ? this.spacing.md : '',
          paddingBottom: this.spacing.md,
        })}
      >
        <x-link
          flex-end
          @click=${() => isSidebarOpen.next(!this.sidebarOpen.value)}
          ${css({
            fontSize: '24px',
            width: '24px',
            height: '24px',
            userSelect: 'none',
          })}
        >
          <x-icon> ${!this.sidebarOpen.value ? 'menu' : 'menu_open'} </x-icon>
          <x-tooltip> <x-text> Sidebar öffnen/schließen </x-text> </x-tooltip>
        </x-link>
        ${this.sidebarOpen.value ? ILSServerTime() : ''}
      </x-flex>
    `;
  }

  private sidebarElement(page: pageType) {
    const color = startRouter.currentRoute.value.route.name === page.page ? this.colors.accent : '';
    const link =
      page.customURL ??
      `/start/${page.page}${searchParamsSignal.value.get('day') ? `?day=${searchParamsSignal.value.get('day')}` : ''}`;

    if (!this.sidebarOpen.value)
      return html`
        <x-link href="${link}" ${css({ userSelect: 'none' })}>
          <x-flex center crossaxis-center padding="sm">
            <x-icon ${css({ color, fontSize: '22px' })}> ${page.icon ?? 'info_outline'} </x-icon>
            <x-tooltip> <x-text> ${clientTranslator.translate('page', page.pageKey, 'title')} </x-text> </x-tooltip>
          </x-flex>
        </x-link>
      `;

    return html`
      <x-sidebar-item ${css({ background: color })} icon=${page.icon ?? 'info_outline'} href=${link}>
        <x-text bold ${css({ display: this.sidebarOpen.value ? 'inherit' : 'none' })}>
          ${clientTranslator.translate('page', page.pageKey, 'title')}
        </x-text>
      </x-sidebar-item>
    `;
  }

  private topbar() {
    if (startRouter.currentRoute.value.route.name === 'exam') return nothing;
    return html`
      <x-flex
        horizontal
        crossaxis-center
        space="md"
        padding="md"
        ${css({
          borderBottom: `1px solid ${this.colors.neutral.shade(200)}`,
          background: 'rgb(216, 216, 230, 0.2)',
        })}
      >
        ${this.size.mobile
          ? html` <x-button mode="icon" icon="menu" @click=${() => XSidebar.toggle()}></x-button> `
          : nothing}
        <x-flex
          flex
          space="xs"
          ${css({
            whiteSpace: 'nowrap',
          })}
        >
          <x-h1> ${startRouterPagename.value} </x-h1>
        </x-flex>
        <x-flex horizontal space="md">
          ${!hasRole(this.user.value, UserRole.TRAINEE)
            ? html`
                <x-button href="tel:${config.get('TECHNICALSUPPORTPHONE')}" target="_blank" mode="outline" icon="call">
                  ${config.get('TECHNICALSUPPORTPHONE')}
                </x-button>
              `
            : nothing}
        </x-flex>
      </x-flex>
    `;
  }
}
